/**
 * Shell - Region resolver
 * Project: Quantiix Shell (quantiix-shell)
 * Module: src/services/regionService.js
 *
 * Asks the global entry host which regional backend this user belongs to.
 *
 *     GET /api/region?email=<address>
 *     200 → { region, endpoints: { gateway, sse } }
 *
 * The path is **relative on purpose**. `/api/region` is served by the
 * same host that serves the SPA, so in staging and production this is
 * same-origin: no CORS, no preflight. Locally the vite dev server proxies it to
 * `VITE_ENTRY_URL` (see `vite.config.js`), which keeps app code free of any
 * hostname — build-time or otherwise.
 *
 * Rules from the contract:
 *   - Always 200 with both endpoints. An unknown email gets the default region;
 *     that is a success, not an error.
 *   - No fallback, no retry, no second region. One call, right first time.
 *   - Rate limited per source address — hence the per-email memo below, so a
 *     mistyped password does not resolve again on every retry.
 */

import { createLogger } from '../utils/logger';

const logger = createLogger('Shell::regionService');

const REGION_PATH = '/api/region';

/** Failure kinds a caller may want to tell apart. */
export const RegionErrorCode = {
    RATE_LIMITED: 'RATE_LIMITED',
    UNAVAILABLE: 'UNAVAILABLE',
    MALFORMED: 'MALFORMED'
};

export class RegionResolveError extends Error {
    constructor(code, message, status) {
        super(message);
        this.name = 'RegionResolveError';
        this.code = code;
        this.status = status ?? null;
    }
}

/**
 * Successful resolves for this page's lifetime, keyed by normalised email.
 * Deliberately not persisted — `runtimeEndpoints` owns the device cache.
 */
const memo = new Map();

const normalise = (email) => String(email ?? '').trim().toLowerCase();

/**
 * Resolve the regional endpoints for an email address.
 *
 * @param {string} email
 * @returns {Promise<{region: string, gateway: string, sse: string}>}
 * @throws {RegionResolveError}
 */
export const resolveRegion = async (email) => {
    const key = normalise(email);

    if (!key) {
        throw new RegionResolveError(
            RegionErrorCode.MALFORMED,
            'Cannot resolve a region without an email address'
        );
    }

    if (memo.has(key)) {
        logger.info('Region already resolved this session — reusing');
        return memo.get(key);
    }

    const url = `${REGION_PATH}?email=${encodeURIComponent(key)}`;
    logger.info('Resolving region...');

    let response;
    try {
        response = await fetch(url, {
            method: 'GET',
            headers: { Accept: 'application/json' }
        });
    } catch (error) {
        // Network-level failure: DNS, offline, TLS. Not retried by design.
        throw new RegionResolveError(
            RegionErrorCode.UNAVAILABLE,
            `Region resolver unreachable: ${error.message}`
        );
    }

    if (response.status === 429) {
        throw new RegionResolveError(
            RegionErrorCode.RATE_LIMITED,
            'Region resolver rate limited this address',
            429
        );
    }

    if (!response.ok) {
        throw new RegionResolveError(
            RegionErrorCode.UNAVAILABLE,
            `Region resolver returned ${response.status}`,
            response.status
        );
    }

    let payload;
    try {
        payload = await response.json();
    } catch {
        throw new RegionResolveError(
            RegionErrorCode.MALFORMED,
            'Region resolver returned a non-JSON body',
            response.status
        );
    }

    const gateway = payload?.endpoints?.gateway;
    const sse = payload?.endpoints?.sse;

    if (typeof gateway !== 'string' || !gateway || typeof sse !== 'string' || !sse) {
        throw new RegionResolveError(
            RegionErrorCode.MALFORMED,
            'Region resolver omitted gateway or sse',
            response.status
        );
    }

    const resolved = { region: payload.region ?? null, gateway, sse };

    memo.set(key, resolved);
    logger.info(`Region resolved: ${resolved.region}`);

    return resolved;
};

/** Drop the in-session memo. Used on logout and by tests. */
export const clearRegionMemo = () => memo.clear();
